import { startTransition, useEffect, useMemo, useRef, useState } from "react";
import { deScore, outcomeFor } from "./dealEngine";
import { useVirtualizer } from "@tanstack/react-virtual";
import { fmtMoney, type Lead } from "../../types";
import { natKey } from "../../lib/natkey";
import { V1_SOURCES, type AddPropertyForm } from "./api";
import { AddPropertyModal } from "./AddPropertyModal";
import { computeDups } from "./dups";
import { idbGet, idbSet } from "../../lib/idb";
import { isHotReply, statusOf, STATUS_OPTIONS, arvOverrideOf, parseMoneyEdit, type EditsMap, type TaskRec } from "../edits/api";
import { narrowRehabCalc, rehabTotal } from "../rehab/api";
import { leadRoi } from "../roi/estMoney";
import { LeadDrawer } from "./LeadDrawer";
import { ConfirmDelete } from "../common/ConfirmDelete";
import { applyViewSpec, type SharedView, type ViewSpec } from "../views/api";
import { leadVisibleTo } from "../../lib/access";

/** a lead's assigned owner id (v1 leadOwner) — the lead drawer already stores it
 * on the lead's edit blob as `assignee` (through the writable lead_fields door, so
 * no new settings key is needed); the scope filter reads that same field. */
const leadOwnerOf = (edits: EditsMap, address: string): string => {
  const v = edits.get(natKey(address))?.["assignee"];
  return typeof v === "string" ? v : "";
};

type SortKey = "newest" | "arvNewest" | "arvPct" | "price" | "priceDesc" | "sqft" | "dealScore" | "roi";
const SORT_KEYS = new Set<SortKey>(["newest", "arvNewest", "arvPct", "price", "priceDesc", "sqft", "dealScore", "roi"]);
const asSortKey = (v: unknown): SortKey | null => (typeof v === "string" && SORT_KEYS.has(v as SortKey) ? (v as SortKey) : null);

/** Saved views are DEVICE-LOCAL on purpose (see MIGRATION_CRM2_PHASE2.sql:
 * v1 boot-injects rec_saved_views rows into its own views UI expecting its
 * positional-array shape — v2 stays out of that table until the shared
 * shape is pinned). */
interface SavedView {
  name: string;
  q: string;
  source: string;
  sort: SortKey;
  status: string;
  onMarket?: boolean; // additive (older saved views lack them — default off)
  maxPrice?: number | null;
  distressed?: boolean; // additive — only-distressed toggle (older views default off)
  jobTag?: string; //      additive — scraper job-tag filter (older views default "all")
}
const VIEWS_KEY = "views_v1";

export function LeadsPage({
  leads,
  syncNote,
  edits,
  tasks,
  onStatus,
  onNotes,
  onArv,
  onField,
  onBulkStatus,
  onBulkDelete,
  sharedViews,
  onSaveSharedView,
  onDeleteSharedView,
  onAddTask,
  onToggleTask,
  onPatchTask,
  onRemoveTask,
  openAddress,
  onOpenedAddress,
  onOpenChange,
  initialLeadTab = null,
  onLeadTabChange,
  onEnrich,
  threads,
  onSendSms,
  onCallLead,
  pastContacts,
  outcomeLog,
  onCompsPersist,
  onCompsArv,
  compsSearch,
  compsPhotos,
  onRunArv,
  onSaveArvResult,
  mailLoaders,
  onAiPermitSearch,
  dealKeys,
  wholesaleKeys,
  onConvert,
  token,
  onAddProperty,
  onWake,
  buyers,
  onSaveRehab,
  onSaveSeller,
  onRenameLead,
  onSaveMediaLinks,
  nrMaster,
  nrBy,
  onSaveNrOverride,
  uiPrefs,
  onSaveUiPrefs,
  hiddenFields,
  onGoInvestors,
  members,
  leadScope = "all",
  myId = null,
  isAdmin = false,
}: {
  leads: Lead[];
  syncNote: string | null;
  edits: EditsMap;
  tasks: TaskRec[];
  onStatus: (address: string, status: string) => void;
  onNotes: (address: string, notes: string) => void;
  onArv: (address: string, kind: "listedArv" | "compedArv", value: number | null) => void;
  onField: (address: string, key: string, value: string | null) => void;
  onBulkStatus: (addresses: string[], status: string) => void;
  /** v1 batchDelete: tombstone + dismiss + drop locally (typed-confirm first) */
  onBulkDelete: (leads: Lead[]) => void;
  /** custom views shared with v1 (rec_saved_views) */
  sharedViews: SharedView[];
  onSaveSharedView: (name: string, spec: import("../views/api").ViewSpec) => void;
  onDeleteSharedView: (name: string) => void;
  onAddTask: (title: string, opts?: { deal?: string; due?: string; who?: string; pri?: string }) => void;
  onToggleTask: (key: string, done: boolean) => void;
  onPatchTask?: (key: string, patch: { who?: string; due?: string; pri?: string }) => void;
  onRemoveTask?: (key: string) => void;
  openAddress?: string | null;
  onOpenedAddress?: () => void;
  onOpenChange?: (address: string | null) => void; // report the open lead (view persistence)
  /** restore the property drawer's inner tab after a refresh */
  initialLeadTab?: string | null;
  onLeadTabChange?: (tab: string) => void;
  /** v1 recheckMarket — Zillow/county/flood/permits pull for one lead */
  onEnrich?: (lead: Lead, onStep: (msg: string) => void) => Promise<string>;
  /** comms threads + live SMS/call handlers for the drawer's Messages card */
  threads?: import("../inbox/api").Thread[];
  onSendSms?: (to: string, text: string, leadKey: string, name?: string, idemKey?: string) => Promise<void>;
  onCallLead?: (to: string, leadKey: string, name?: string) => Promise<void>;
  /** past title/dispo contacts for the one-pick fill (2026-08-31) */
  pastContacts?: import("../contacts/pastContacts").PastContacts | undefined;
  /** rec_org_settings OUTCOME_LOG — back-on-market signal for the Deal Engine */
  outcomeLog: unknown;
  onCompsPersist: (address: string, state: { filters: import("../comps/api").CompsFilters; saved: Record<string, import("../comps/api").SavedComp> }) => void;
  onCompsArv: (address: string, arv: number) => void;
  compsSearch: (address: string, f: import("../comps/api").CompsFilters, onWake: () => void) => Promise<{ comps: import("../comps/api").CompRow[]; subject: import("../comps/api").CompsSubject | null }>;
  compsPhotos: (comp: import("../comps/api").CompRow) => Promise<string[]>;
  onRunArv: (address: string, sqft: number, beds: number | null, onWake: () => void) => Promise<import("../arv/api").ArvResult>;
  onSaveArvResult: (address: string, result: import("../arv/api").ArvResult) => void;
  mailLoaders: import("../mail/EmailsSection").MailLoaders;
  onAiPermitSearch: (address: string, county: string, leadId: string) => Promise<import("../permits/api").AiPermitResult>;
  dealKeys: Set<string>;
  wholesaleKeys: Set<string>;
  onConvert: (lead: Lead, kind: "flip" | "wholesale") => void;
  token: string;
  /** Add Property (v1 manual add). Create is Phase-B-gated inside the modal. */
  onAddProperty: (payload: { form: AddPropertyForm; zillowUrl: string | null }) => void;
  onWake?: () => void;
  /** dispo rolodex for the drawer's Matched Buyers panel (v1 matchedBuyersPanel) */
  buyers: import("../buyers/api").Buyer[];
  /** persist the itemized rehab sheet (v1 rehabCalc) through the lead_fields door */
  onSaveRehab: (address: string, state: import("../rehab/api").RehabCalcState, total: number) => void;
  /** save/clear the seller contact card (v1 saveSeller/clearSeller) */
  onSaveSeller: (address: string, seller: import("../edits/api").SellerContact | null) => void;
  /** rename the lead's ADDRESS (its identity key) — server re-keys every table */
  onRenameLead?: (oldAddress: string, newAddress: string) => Promise<void>;
  onSaveMediaLinks: (address: string, links: import("../edits/api").MediaLink[]) => void;
  nrMaster: import("../roi/math").NrInputs;
  /** org NR_BY blob + its writer — forwarded to the drawer's Net ROI tab so a
   *  per-property override can be SAVED, not just read (this was v1-only). */
  nrBy?: unknown;
  onSaveNrOverride?: (address: string, vals: Record<string, number>) => void;
  /** per-user pinned lead-drawer tabs, forwarded to the drawer */
  uiPrefs?: import("../settings/uiPrefs").UiPrefs;
  onSaveUiPrefs?: (next: import("../settings/uiPrefs").UiPrefs) => void;
  hiddenFields: Set<string>;
  onGoInvestors?: () => void;
  members?: { id: string; name: string }[];
  /** the current user's lead scope ("all" | "assigned") + id, for v1 leadVisible */
  leadScope?: string;
  myId?: string | null;
  /** admins may (re)assign a lead to a team member */
  isAdmin?: boolean;
}) {
  const [q, setQ] = useState("");
  const [source, setSource] = useState<string>("all");
  const [status, setStatus] = useState<string>("all");
  const [sort, setSort] = useState<SortKey>("newest");
  const [onMarket, setOnMarket] = useState(false);
  const [distressed, setDistressed] = useState(false);
  const [jobTag, setJobTag] = useState<string>("all");
  const [maxPrice, setMaxPrice] = useState<string>(""); // raw input; parsed for the filter
  const [views, setViews] = useState<SavedView[]>([]);
  const [activeView, setActiveView] = useState<string | null>(null);
  const [naming, setNaming] = useState(false);
  const [viewName, setViewName] = useState("");
  // "New view" builder fields (Tom 2026-08-14: the + should let you pick date
  // added / source / status / market / price / sort, not just name the current
  // filters). applyViewSpec already honors every one of these, so the builder
  // just constructs a ViewSpec and saves it as a shared view.
  const [bvDate, setBvDate] = useState("any");     // any | today | yesterday | 7d | 30d
  const [bvSource, setBvSource] = useState("all");
  const [bvStatus, setBvStatus] = useState("all");
  const [bvMarket, setBvMarket] = useState("any"); // any | on | off
  const [bvSort, setBvSort] = useState<string>("newest");
  const [bvMax, setBvMax] = useState("");
  const openBuilder = () => {
    // pre-fill from the current filter bar so "+" doubles as "save what I'm
    // looking at", then let the fields be tweaked.
    setBvSource(source); setBvStatus(status); setBvSort(sort); setBvMax(maxPrice);
    setBvMarket(onMarket ? "on" : "any"); setBvDate("any"); setViewName("");
    setNaming(true);
  };
  const createView = () => {
    const name = viewName.trim();
    if (!name) return;
    const spec: import("../views/api").ViewSpec = {};
    if (bvStatus !== "all") spec.statuses = [bvStatus];
    if (bvSource !== "all") spec.sources = [bvSource];
    if (bvMarket === "on") spec.market = "On Market";
    else if (bvMarket === "off") spec.market = "Off Market";
    const mp = parseFloat(bvMax.replace(/[^0-9.]/g, ""));
    if (Number.isFinite(mp) && mp > 0) spec.pmax = mp;
    if (bvSort !== "newest") spec.sort = bvSort;
    if (bvDate === "today") spec.createdWithin = 0;
    else if (bvDate === "7d") spec.createdWithin = 7;
    else if (bvDate === "30d") spec.createdWithin = 30;
    else if (bvDate === "yesterday") {
      const d = new Date(); d.setDate(d.getDate() - 1);
      const y = d.toISOString().slice(0, 10);
      spec.createdBetween = [y, y];
    }
    onSaveSharedView(name, spec);      // persists + optimistically joins sharedViews
    applyView(null);
    setActiveShared(name);
    const sk = asSortKey(spec.sort);
    if (sk) setSort(sk);
    setNaming(false); setViewName("");
  };
  const [openLead, setOpenLead] = useState<Lead | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set()); // lead ids
  const [bulkTo, setBulkTo] = useState("");
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleteOne, setDeleteOne] = useState<Lead | null>(null);
  const [activeShared, setActiveShared] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const parentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    void idbGet<SavedView[]>(VIEWS_KEY).then((v) => Array.isArray(v) && setViews(v));
  }, []);

  // cross-page jump (e.g. Inbox → "View lead") + view-restore on refresh: open
  // the drawer for an address. Wait for `leads` to load before giving up — the
  // restore fires before the async fetch resolves, so clearing on an empty list
  // would drop the target. Clear only once found, or once loaded-but-absent.
  useEffect(() => {
    if (!openAddress) return;
    const hit = leads.find((l) => natKey(l.address) === natKey(openAddress));
    if (hit) { setOpenLead(hit); onOpenedAddress?.(); }
    else if (leads.length) onOpenedAddress?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openAddress, leads]);

  // report the open lead so a refresh can reopen it (view persistence)
  useEffect(() => { onOpenChange?.(openLead ? openLead.address : null); }, [openLead]); // eslint-disable-line react-hooks/exhaustive-deps

  // v1 parity: the fixed SOURCES list, plus anything present in the data
  const sources = useMemo(
    () => ["all", ...new Set([...leads.map((l) => l.source), ...V1_SOURCES])],
    [leads],
  );
  // Distinct scraper job tags present on the leads (v1 JOB_TAGS filter). Only
  // rendered when at least one lead carries a tag, so the bar stays clean.
  const jobTags = useMemo(() => {
    const set = new Set<string>();
    for (const l of leads) if (l.jobTag) set.add(l.jobTag);
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [leads]);
  const statuses = useMemo(() => {
    const extra = new Set<string>();
    for (const f of edits.values()) {
      const s = statusOf(f);
      if (!STATUS_OPTIONS.includes(s)) extra.add(s);
    }
    return ["all", ...STATUS_OPTIONS, ...extra];
  }, [edits]);

  // Effective status per lead, computed ONCE (natKey + edits lookup + statusOf).
  // The filter, the dup badges, and every per-view count below all need it; before
  // this each recomputed it per lead, and natKey is a hot path — on a 4.7k-lead
  // phone that made opening Properties sluggish. One pass, shared everywhere.
  const effStatus = useMemo(() => {
    const m = new Map<string, string>();
    for (const l of leads) m.set(l.id, statusOf(edits.get(natKey(l.address))));
    return m;
  }, [leads, edits]);

  // v1 badges: dup groups over ALL leads (not just filtered) + 🔥 window
  const dups = useMemo(
    () => computeDups(leads, (l) => effStatus.get(l.id) ?? ""),
    [leads, effStatus],
  );

  // Sidebar view order (Tom 2026-08-13): "New Untouched" right under All
  // Properties, then every "Added Today - *" grouped, then every "Added
  // Yesterday - *" grouped (Investorlift, Zillow, Email, SMS), then whatever's
  // left in its saved order. Also injects "Added Yesterday - Investorlift" — it
  // was never saved so it was missing — copying an existing "Added Yesterday - *"
  // spec (so the date window matches the others) and swapping in Investorlift's
  // source from the working "Added Today - Investorlift" view.
  type OrderedView = SharedView & { builtin?: boolean };
  const orderedViews = useMemo<OrderedView[]>(() => {
    const srcRank = (s: string) =>
      s.includes("investorlift") ? 0 : s.includes("zillow") ? 1 : s.includes("email") ? 2 : s.includes("sms") ? 3 : 4;
    const rank = (name: string): [number, number] => {
      const s = name.trim().toLowerCase();
      if (s.startsWith("new untouched")) return [0, 0];
      if (s.startsWith("added today")) return [1, srcRank(s)];
      if (s.startsWith("added yesterday")) return [2, srcRank(s)];
      return [3, 0]; // everything else keeps its saved order (stable sort)
    };
    const list: OrderedView[] = [...sharedViews];
    if (!list.some((v) => v.name.trim().toLowerCase() === "added yesterday - investorlift")) {
      const tmpl = sharedViews.find((v) => v.name.trim().toLowerCase().startsWith("added yesterday"));
      const todayIL = sharedViews.find((v) => v.name.trim().toLowerCase() === "added today - investorlift");
      const spec: ViewSpec = { ...(tmpl?.spec ?? {}) };
      delete spec.source;
      delete spec.sources;
      if (todayIL?.spec.sources?.length) spec.sources = [...todayIL.spec.sources];
      else spec.source = todayIL?.spec.source ?? "Investorlift";
      if (!spec.createdBetween && spec.createdWithin === undefined && spec.listedBetween === undefined) {
        const y = new Date(Date.now() - 86400000);
        const ymd = `${y.getFullYear()}-${String(y.getMonth() + 1).padStart(2, "0")}-${String(y.getDate()).padStart(2, "0")}`;
        spec.createdBetween = [ymd, ymd];
      }
      list.push({ name: "Added Yesterday - Investorlift", hasCircles: false, builtin: true, spec });
    }
    return list
      .map((v, i) => ({ v, i, r: rank(v.name) }))
      .sort((a, b) => a.r[0] - b.r[0] || a.r[1] - b.r[1] || a.i - b.i)
      .map((x) => x.v);
  }, [sharedViews]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const maxP = parseFloat(maxPrice.replace(/[^0-9.]/g, ""));
    const sharedSpec = activeShared !== null ? orderedViews.find((v) => v.name === activeShared)?.spec ?? null : null;
    const nowMs = Date.now();
    const out = leads.filter((l) => {
      // lead-scope enforcement (v1 leadVisible): a restricted "assigned" user only
      // sees leads assigned to them. Inert for the current all-manager/member team
      // (leadScope resolves to "all" for them).
      if (!leadVisibleTo(leadScope, myId, leadOwnerOf(edits, l.address))) return false;
      if (sharedSpec) {
        const eff = effStatus.get(l.id) ?? "";
        const dup = dups.get(l.id)?.count ?? 1;
        if (!applyViewSpec(l, eff, dup, sharedSpec, nowMs)) return false;
      }
      if (source !== "all" && l.source !== source) return false;
      if (status !== "all" && (effStatus.get(l.id) ?? "") !== status) return false;
      // v1 getProps: PF.onMarket / PF.maxPrice (no-price rows pass, like v1's money()=0)
      if (onMarket && l.marketStatus !== "on") return false;
      if (distressed && !l.distressed) return false;
      if (jobTag !== "all" && (l.jobTag ?? "") !== jobTag) return false;
      if (Number.isFinite(maxP) && maxP > 0 && (l.price ?? 0) > maxP) return false;
      // v1 parity (getProps): needle matches address + seller/agent + county
      if (
        needle &&
        !l.address.toLowerCase().includes(needle) &&
        !(l.county ?? "").toLowerCase().includes(needle) &&
        !(l.seller ?? "").toLowerCase().includes(needle)
      ) return false;
      return true;
    });
    switch (sort) {
      case "arvPct":
        out.sort((a, b) => (a.arvPct ?? 999) - (b.arvPct ?? 999));
        break;
      case "price":
        out.sort((a, b) => (a.price ?? Infinity) - (b.price ?? Infinity));
        break;
      case "priceDesc":
        out.sort((a, b) => (b.price ?? 0) - (a.price ?? 0));
        break;
      case "sqft":
        out.sort((a, b) => (b.sqft ?? 0) - (a.sqft ?? 0));
        break;
      case "arvNewest":
        // v1 (2026-07-15, Tom): newest DAY first, lowest ARV% within each day
        out.sort((a, b) => {
          const da = Math.floor(a.createdAt / 86400000), db = Math.floor(b.createdAt / 86400000);
          if (db !== da) return db - da;
          return (a.arvPct ?? 999) - (b.arvPct ?? 999);
        });
        break;
      case "dealScore": {
        const sc = new Map<string, number>();
        for (const l of out) sc.set(l.id, deScore(l, null, nowMs, outcomeFor(outcomeLog, l.address)));
        out.sort((a, b) => (sc.get(b.id) ?? 0) - (sc.get(a.id) ?? 0));
        break;
      }
      case "roi": {
        // projected flip ROI per lead (same flipResult the drawer's Est-ROI tile
        // runs) — priced+ARV'd leads first, unknowns sink to the bottom.
        const sc = new Map<string, number>();
        for (const l of out) {
          const f = edits.get(natKey(l.address));
          const purchase = parseMoneyEdit(typeof f?.["offerPrice"] === "string" ? (f["offerPrice"] as string) : null) ?? l.price ?? 0;
          const arv = arvOverrideOf(f) ?? l.arv;
          const roi = leadRoi(purchase, arv, rehabTotal(narrowRehabCalc(f?.["rehabCalc"]), l), nrMaster).roi;
          sc.set(l.id, roi ?? -Infinity);
        }
        out.sort((a, b) => (sc.get(b.id) ?? -Infinity) - (sc.get(a.id) ?? -Infinity));
        break;
      }
      default:
        out.sort((a, b) => b.createdAt - a.createdAt);
    }
    return out;
  }, [leads, edits, effStatus, q, source, status, sort, onMarket, distressed, jobTag, maxPrice, activeShared, orderedViews, dups, outcomeLog, nrMaster, leadScope, myId]);

  // Per-view counts for the v1-style Views panel: apply each shared view's spec
  // to every lead (ignoring the current search/status/source filters), like v1's
  // _propViewCount which counts the view itself, not the active filter.
  const viewCounts = useMemo(() => {
    const m = new Map<string, number>();
    const nowMs = Date.now();
    for (const v of orderedViews) {
      let c = 0;
      for (const l of leads) {
        const eff = effStatus.get(l.id) ?? "";
        const dup = dups.get(l.id)?.count ?? 1;
        if (applyViewSpec(l, eff, dup, v.spec, nowMs)) c++;
      }
      m.set(v.name, c);
    }
    return m;
  }, [leads, effStatus, dups, orderedViews]);

  const now = Date.now(); // per-render is fine: badge window is 48h

  // row height matches the CSS breakpoint (96px two-line card on phones,
  // 64px single line on desktop) — the virtualizer must agree with the CSS
  const isNarrow = useMemo(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 720px)").matches,
    [],
  );
  const virt = useVirtualizer({
    count: filtered.length,
    getScrollElement: () => parentRef.current,
    // v1-style cards: taller than the old thin rows. measureElement (below)
    // corrects each card to its real height, so this is only the first guess —
    // measured medians (2026-07-25): phones 177-252 (chips wrap), desktop a
    // uniform 106. A close estimate keeps the mount's measure-cascade tiny.
    estimateSize: () => (isNarrow ? 210 : 106),
    overscan: 5,
  });

  const applyView = (v: SavedView | null) => {
    if (!v) {
      setActiveView(null);
      setQ("");
      setSource("all");
      setStatus("all");
      setSort("newest");
      setOnMarket(false);
      setDistressed(false); // else "All Properties" keeps the Distressed filter on
      setJobTag("all");
      setMaxPrice("");
      return;
    }
    setActiveView(v.name);
    setQ(v.q);
    setSource(v.source);
    setStatus(v.status);
    setSort(v.sort);
    setOnMarket(v.onMarket === true);
    setDistressed(v.distressed === true); // restore, don't force-clear (was always false)
    setJobTag(v.jobTag ?? "all");
    setMaxPrice(v.maxPrice ? String(v.maxPrice) : "");
  };

  const persistViews = (next: SavedView[]) => {
    setViews(next);
    void idbSet(VIEWS_KEY, next);
  };


  const deleteView = (name: string) => {
    persistViews(views.filter((x) => x.name !== name));
    if (activeView === name) applyView(null);
  };

  // Select a shared view: clear the local filter bar, activate the shared spec
  // (applied as a per-lead filter), and honor its saved sort so the ordering the
  // view was saved with is restored (a bare setActiveShared kept "Newest").
  const pickShared = (name: string | null) => {
    applyView(null);
    setActiveShared(name);
    if (name) {
      const sk = asSortKey(orderedViews.find((v) => v.name === name)?.spec.sort);
      if (sk) setSort(sk);
    }
  };

  // keep the drawer's lead object fresh as leads refresh underneath it
  const openLeadLive = useMemo(
    () => (openLead ? leads.find((l) => l.id === openLead.id) ?? openLead : null),
    [openLead, leads],
  );

  // ---- bulk selection over the FILTERED list ---------------------------------
  const toggleSel = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const selectedInView = useMemo(() => filtered.filter((l) => selected.has(l.id)), [filtered, selected]);
  // How many filters are actually narrowing the list. Drives the count on the
  // mobile Filters button — the whole point of collapsing them is that you must
  // never be filtering without knowing it. Search is excluded: it is visible in
  // its own box, so counting it would be telling you something you can see.
  const [filtersOpen, setFiltersOpen] = useState(false);
  const activeFilters =
    (source !== "all" ? 1 : 0) + (status !== "all" ? 1 : 0) + (jobTag !== "all" ? 1 : 0) +
    (sort !== "newest" ? 1 : 0) + (onMarket ? 1 : 0) + (distressed ? 1 : 0) +
    (maxPrice.trim() ? 1 : 0);
  const clearFilters = () => {
    setSource("all"); setStatus("all"); setJobTag("all"); setSort("newest");
    setOnMarket(false); setDistressed(false); setMaxPrice("");
  };

  const allInViewSelected = filtered.length > 0 && selectedInView.length === filtered.length;
  const toggleAllInView = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allInViewSelected) for (const l of filtered) next.delete(l.id);
      else for (const l of filtered) next.add(l.id);
      return next;
    });
  };
  const applyBulk = () => {
    if (!bulkTo || selectedInView.length === 0) return;
    onBulkStatus(selectedInView.map((l) => l.address), bulkTo);
    setSelected(new Set());
    setBulkTo("");
  };

  return (
    <div className={"leads" + (openLeadLive ? "" : " propsplitwrap")}>
      {!openLeadLive && (
        <div className="propsplit">
          <aside className="propviews">
            <div className="propviewshd">
              <span>Views</span>
              {naming ? null : <button className="pvadd" title="New view — pick date added, source, status, market, price, sort" onClick={openBuilder}>+</button>}
            </div>
            <div className="propviewslist">
              <button className={"propview" + (activeView === null && activeShared === null ? " on" : "")} onClick={() => { applyView(null); setActiveShared(null); }}>
                <span className="pvname">All Properties</span>
                <span className="pvcount">{leads.length.toLocaleString()}</span>
              </button>
              {orderedViews.map((v) => {
                const on = activeShared === v.name;
                return (
                  <div key={"sh:" + v.name} className={"propview" + (on ? " on" : "")} title={v.builtin ? "Auto view — Investorlift leads added yesterday" : v.hasCircles ? "Shared view — map circles not applied in v2 yet" : "Shared view (also in v1)"} onClick={() => pickShared(on ? null : v.name)}>
                    <span className="pvname">{v.name}{v.hasCircles ? "°" : ""}</span>
                    <span className="pvright">
                      <span className="pvcount">{(viewCounts.get(v.name) ?? 0).toLocaleString()}</span>
                      {v.builtin ? null : <button className="pvdel" title="Delete this shared view (removes it in v1 too)" onClick={(e) => { e.stopPropagation(); onDeleteSharedView(v.name); if (on) setActiveShared(null); }}>×</button>}
                    </span>
                  </div>
                );
              })}
              {views.map((v) => (
                <div key={v.name} className={"propview" + (activeView === v.name ? " on" : "")} onClick={() => { setActiveShared(null); applyView(v); }}>
                  <span className="pvname">{v.name}</span>
                  <button className="pvdel" title={`Delete view "${v.name}"`} onClick={(e) => { e.stopPropagation(); deleteView(v.name); }}>×</button>
                </div>
              ))}
              {naming ? (
                <div className="propview naming vbuilder" onClick={(e) => e.stopPropagation()}>
                  <input
                    autoFocus
                    placeholder="View name"
                    value={viewName}
                    onChange={(e) => setViewName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") createView();
                      if (e.key === "Escape") setNaming(false);
                    }}
                  />
                  <label className="vbrow"><span>Date added</span>
                    <select value={bvDate} onChange={(e) => setBvDate(e.target.value)}>
                      <option value="any">Any time</option>
                      <option value="today">Today</option>
                      <option value="yesterday">Yesterday</option>
                      <option value="7d">Last 7 days</option>
                      <option value="30d">Last 30 days</option>
                    </select>
                  </label>
                  <label className="vbrow"><span>Source</span>
                    <select value={bvSource} onChange={(e) => setBvSource(e.target.value)}>
                      {sources.map((s) => <option key={s} value={s}>{s === "all" ? "All sources" : s}</option>)}
                    </select>
                  </label>
                  <label className="vbrow"><span>Status</span>
                    <select value={bvStatus} onChange={(e) => setBvStatus(e.target.value)}>
                      {statuses.map((s) => <option key={s} value={s}>{s === "all" ? "All statuses" : s}</option>)}
                    </select>
                  </label>
                  <label className="vbrow"><span>Market</span>
                    <select value={bvMarket} onChange={(e) => setBvMarket(e.target.value)}>
                      <option value="any">On + off market</option>
                      <option value="on">On market</option>
                      <option value="off">Off market</option>
                    </select>
                  </label>
                  <label className="vbrow"><span>Sort</span>
                    <select value={bvSort} onChange={(e) => setBvSort(e.target.value)}>
                      <option value="newest">Newest</option>
                      <option value="arvNewest">Newest + lowest ARV%</option>
                      <option value="arvPct">Lowest ARV%</option>
                      <option value="price">Lowest price</option>
                      <option value="priceDesc">Highest price</option>
                      <option value="sqft">Largest sqft</option>
                      <option value="dealScore">Deal Score</option>
                      <option value="roi">Est. ROI</option>
                    </select>
                  </label>
                  <label className="vbrow"><span>Max $</span>
                    <input className="short" inputMode="numeric" placeholder="Any" value={bvMax} onChange={(e) => setBvMax(e.target.value)} />
                  </label>
                  <div className="vbuilderacts">
                    <button className="pvsave" disabled={!viewName.trim()} onClick={createView}>Create view</button>
                    <button className="btn slim" onClick={() => setNaming(false)}>Cancel</button>
                  </div>
                </div>
              ) : null}
            </div>
          </aside>
          <div className="propmain">
            <div className="propmainhd">
              <div className="propmaintitle">
                <h1>Properties</h1>
                <p>{filtered.length.toLocaleString()} of {leads.length.toLocaleString()} properties{syncNote ? <em className="note"> · {syncNote}</em> : null}</p>
              </div>
              <div className="propmainacts">
                {onGoInvestors ? <button className="vtab investbtn" title="Match properties to an investor's buy-box" onClick={onGoInvestors}>◎ Investor Matches</button> : null}
                <button className="btn primary addprop" title="Add a property by hand (v1 Add Property)" onClick={() => setAddOpen(true)}>+ Add Property</button>
              </div>
            </div>
            <select className="propviewsel" value={activeShared !== null ? "sh:" + activeShared : activeView !== null ? "cv:" + activeView : "__all"} onChange={(e) => {
              const val = e.target.value;
              if (val === "__all") { applyView(null); setActiveShared(null); }
              else if (val.startsWith("sh:")) pickShared(val.slice(3));
              else if (val.startsWith("cv:")) { const cv = views.find((s) => s.name === val.slice(3)); setActiveShared(null); if (cv) applyView(cv); }
            }}>
              <option value="__all">All Properties ({leads.length.toLocaleString()})</option>
              {orderedViews.map((v) => <option key={v.name} value={"sh:" + v.name}>{v.name} ({viewCounts.get(v.name) ?? 0})</option>)}
              {views.map((v) => <option key={v.name} value={"cv:" + v.name}>{v.name}</option>)}
            </select>
      <header className="bar">
        {/* Bulk-select is a mode, not a permanent control. On a phone it sat
            above the search box taking a row of the screen for something you
            only want once you are already selecting — so it now appears with
            the selection, and the desktop layout is unchanged. */}
        <label className={"selall" + (selected.size ? " armed" : "")} title="Select everything matching the current filters">
          <input type="checkbox" checked={allInViewSelected} onChange={toggleAllInView} />
          Select all
        </label>
        <input placeholder="Search address, seller, or county…" value={q} onChange={(e) => setQ(e.target.value)} />
        {/* Mobile only: six filter controls stacked was ~430px before the first
            property. Search stays out — it is the one you reach for — and the
            rest fold into a sheet with a count, so a filter can never be left
            on invisibly. Desktop renders .barfilters inline exactly as before. */}
        <button
          type="button"
          className={"barfilterbtn" + (activeFilters ? " on" : "")}
          aria-expanded={filtersOpen}
          onClick={() => setFiltersOpen(true)}
        >
          Filters{activeFilters ? <span className="barfiltercount">{activeFilters}</span> : null}
        </button>
        {filtersOpen ? <div className="barfilterscrim" onClick={() => setFiltersOpen(false)} /> : null}
        <div className={"barfilters" + (filtersOpen ? " open" : "")}>
          <div className="barfiltershd">
            <span>Filters</span>
            <button type="button" className="barfilterdone" onClick={() => setFiltersOpen(false)}>Done</button>
          </div>
        <select value={source} onChange={(e) => setSource(e.target.value)}>
          {sources.map((s) => (
            <option key={s} value={s}>{s === "all" ? "All sources" : s}</option>
          ))}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)}>
          {statuses.map((s) => (
            <option key={s} value={s}>{s === "all" ? "All statuses" : s}</option>
          ))}
        </select>
        {jobTags.length ? (
          <select value={jobTag} onChange={(e) => setJobTag(e.target.value)} title="Filter by the scraper job that produced the lead">
            <option value="all">All job tags</option>
            {jobTags.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        ) : null}
        <select value={sort} onChange={(e) => setSort(e.target.value as SortKey)}>
          <option value="newest">Newest</option>
          <option value="arvNewest">Newest + lowest ARV%</option>
          <option value="arvPct">Lowest ARV%</option>
          <option value="price">Lowest price</option>
          <option value="priceDesc">Highest price</option>
          <option value="sqft">Largest sqft</option>
          <option value="dealScore">Deal Score ↓</option>
          <option value="roi">Est. ROI ↓</option>
        </select>
        <button type="button" className={"filtertoggle" + (onMarket ? " on" : "")} title="Only leads currently on the market" onClick={() => setOnMarket(!onMarket)}>On market</button>
        <button type="button" className={"filtertoggle" + (distressed ? " on" : "")} title="Only distressed leads (v1 distressed filter)" onClick={() => setDistressed(!distressed)}>Distressed</button>
        <input
          className="short"
          inputMode="numeric"
          placeholder="Max $"
          value={maxPrice}
          onChange={(e) => setMaxPrice(e.target.value)}
        />
        {activeFilters ? (
          <button type="button" className="barfilterclear" onClick={clearFilters}>Clear all filters</button>
        ) : null}
        </div>
      </header>
      {selectedInView.length > 0 ? (
        <div className="bulkbar">
          <span>
            <strong>{selectedInView.length.toLocaleString()}</strong> selected
          </span>
          <select value={bulkTo} onChange={(e) => setBulkTo(e.target.value)}>
            <option value="">Set status to…</option>
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <button className="btn" disabled={!bulkTo} onClick={applyBulk}>Apply</button>
          <button className="btn danger" onClick={() => setConfirmingDelete(true)}>Delete</button>
          <button className="vtab" onClick={() => setSelected(new Set())}>Clear</button>
        </div>
      ) : null}
      {confirmingDelete ? (
        <ConfirmDelete
          message={`Delete ${selectedInView.length} selected propert${selectedInView.length === 1 ? "y" : "ies"}? They will be tombstoned so scrapers can't bring them back.`}
          onCancel={() => setConfirmingDelete(false)}
          onConfirm={() => {
            const chosen = filtered.filter((l) => selected.has(l.id));
            setConfirmingDelete(false);
            setSelected(new Set());
            onBulkDelete(chosen);
          }}
        />
      ) : null}
      {deleteOne ? (
        <ConfirmDelete
          message={`Delete ${deleteOne.address}? It will be tombstoned so scrapers can't bring it back.`}
          onCancel={() => setDeleteOne(null)}
          onConfirm={() => {
            const chosen = deleteOne;
            setDeleteOne(null);
            onBulkDelete([chosen]);
          }}
        />
      ) : null}
      <div ref={parentRef} className="list cards">
        <div style={{ height: virt.getTotalSize(), position: "relative" }}>
          {virt.getVirtualItems().map((row) => {
            const l = filtered[row.index];
            if (!l) return null;
            const f = edits.get(natKey(l.address));
            const st = statusOf(f);
            const dup = dups.get(l.id);
            const hot = isHotReply(f, now);
            const good = l.arvPct !== null && l.arvPct <= 70;
            const dt = new Date(l.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
            return (
              <div
                key={l.id}
                ref={virt.measureElement}
                data-index={row.index}
                className="lprow"
                style={{ position: "absolute", top: 0, left: 0, width: "100%", transform: `translateY(${row.start}px)` }}
              >
                {/* MOBILE PERF: opening the drawer mounts a big component — as a
                    transition the tap responds instantly on slow CPUs */}
                <div className="lpcard" onClick={() => startTransition(() => setOpenLead(l))}>
                  <input
                    type="checkbox"
                    className="lpsel"
                    checked={selected.has(l.id)}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => toggleSel(l.id)}
                  />
                  <div className="lpthumb">
                    {l.photos && l.photos[0] ? <img src={l.photos[0]} alt="" loading="lazy" /> : "🏠"}
                  </div>
                  <div className="lpmain">
                    <div className="lpchips">
                      <span className="lchip source">{l.source}</span>
                      {l.marketStatus === "on" ? (
                        <span className="lchip market on">● On Market</span>
                      ) : (
                        <span className="lchip market">● Off Market</span>
                      )}
                      {hot ? <span className="lchip hot" title="Hot reply from the Text Agent (last 48h)">🔥 HOT reply</span> : null}
                      {l.priceDrop ? (
                        <span className="lchip drop" title={`From ${fmtMoney(l.priceDrop.from)} to ${fmtMoney(l.priceDrop.to)}`}>↓ {l.priceDrop.pct}%</span>
                      ) : l.reductions > 0 ? (
                        <span className="lchip drop" title={`${l.reductions} reduction${l.reductions > 1 ? "s" : ""} recorded`}>{l.reductions} Reduction{l.reductions > 1 ? "s" : ""}</span>
                      ) : null}
                      {dup ? <span className="lchip dup" title={`Also in: ${dup.where}`}>⧉ Duplicate ×{dup.count}</span> : null}
                      <select
                        className={"lstatus s-" + st.toLowerCase().replace(/[^a-z]+/g, "-")}
                        value={st}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => onStatus(l.address, e.target.value)}
                        title="Change status"
                      >
                        {(STATUS_OPTIONS.includes(st) ? STATUS_OPTIONS : [st, ...STATUS_OPTIONS]).map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </div>
                    <div className="lpaddr">{l.address}</div>
                    <div className="lpmeta">
                      {l.seller ? <span className="mi">👤 {l.seller}</span> : null}
                      <span className="strong">{fmtMoney(l.price)}</span>
                      {l.arvPct !== null ? <span>Asking/ARV: <span className={good ? "good" : "strong"}>{l.arvPct}%</span></span> : null}
                      {l.cdom !== null ? <span>CDOM: {l.cdom}d</span> : null}
                      <span>ARV: <span className="strong">{fmtMoney(l.arv)}</span></span>
                      {l.zest !== null ? <span>Zest: {fmtMoney(l.zest)}</span> : null}
                      {l.county ? <span className="mi">◉ {l.county}</span> : null}
                      <span className="mi">📅 {dt}</span>
                    </div>
                  </div>
                  <button
                    className="lpdel"
                    title="Delete this property"
                    onClick={(e) => { e.stopPropagation(); setDeleteOne(l); }}
                  >🗑</button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
          </div>
        </div>
      )}
      {openLeadLive ? (
        <LeadDrawer
          lead={openLeadLive}
          edits={edits}
          tasks={tasks}
          outcomeLog={outcomeLog}
          onClose={() => setOpenLead(null)}
          dupOthers={dups.get(openLeadLive.id)?.others ?? []}
          onOpenDup={(addr) => {
            const hit = leads.find((x) => x.address === addr);
            if (hit) setOpenLead(hit);
          }}
          onStatus={onStatus}
          onNotes={onNotes}
          onArv={onArv}
          onField={onField}
          onAddTask={onAddTask}
          onToggleTask={onToggleTask}
          {...(onPatchTask ? { onPatchTask } : {})}
          {...(onRemoveTask ? { onRemoveTask } : {})}
          onCompsPersist={onCompsPersist}
          onCompsArv={onCompsArv}
          compsSearch={compsSearch}
          compsPhotos={compsPhotos}
          onRunArv={onRunArv}
          onSaveArvResult={onSaveArvResult}
          mailLoaders={mailLoaders}
          onAiPermitSearch={onAiPermitSearch}
          dealKeys={dealKeys}
          wholesaleKeys={wholesaleKeys}
          onConvert={onConvert}
          buyers={buyers}
          onSaveRehab={onSaveRehab}
          onSaveSeller={onSaveSeller}
          {...(onRenameLead ? { onRenameLead } : {})}
          onSaveMediaLinks={onSaveMediaLinks}
          nrMaster={nrMaster}
          {...(nrBy !== undefined ? { nrBy } : {})}
          {...(onSaveNrOverride ? { onSaveNrOverride } : {})}
          {...(uiPrefs ? { uiPrefs } : {})}
          {...(onSaveUiPrefs ? { onSaveUiPrefs } : {})}
          hiddenFields={hiddenFields}
          onDelete={(l) => { onBulkDelete([l]); setOpenLead(null); }}
          members={members ?? []}
          isAdmin={isAdmin}
          token={token}
          initialTab={initialLeadTab}
          {...(onLeadTabChange ? { onTabChange: onLeadTabChange } : {})}
          {...(onEnrich ? { onEnrich } : {})}
          {...(threads ? { threads } : {})}
          {...(onSendSms ? { onSendSms } : {})}
          {...(onCallLead ? { onCallLead } : {})}
          pastContacts={pastContacts}
        />
      ) : null}
      {addOpen ? (
        <AddPropertyModal
          token={token}
          onClose={() => setAddOpen(false)}
          onCreate={(payload) => { onAddProperty(payload); setAddOpen(false); }}
          {...(onWake ? { onWake } : {})}
        />
      ) : null}
    </div>
  );
}
